import { axiosClient } from '@/shared/apis/config';
import { DtoFolderItemType } from '@/shared/constants';
import { ApiResponse } from '@/shared/types/api/response';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * 벌크 북마크 추가 요청 스키마
 */
export const bookmarkBulkAddRequestSchema = z.object({
  items: z.array(
    z.object({
      targetType: z.nativeEnum(DtoFolderItemType),
      targetId: z.number(),
    }),
  ),
});

export type BookmarkBulkAddRequest = z.infer<
  typeof bookmarkBulkAddRequestSchema
>;

/**
 * 벌크 북마크 추가 응답 스키마
 */
export const bookmarkBulkAddResponseSchema = z.object({
  addedCount: z.number(),
});

export type BookmarkBulkAddResponse = z.infer<
  typeof bookmarkBulkAddResponseSchema
>;

/**
 * 벌크 북마크 추가 API 응답 타입
 */
type PostBookmarkBulkAddApiResponse = ApiResponse<BookmarkBulkAddResponse>;

/**
 * 벌크 북마크 추가 API 함수
 *
 * @param request - 북마크 추가할 아이템 목록
 * @returns 추가된 북마크 개수
 */
export const postBookmarkBulkAddApi = async (
  request: BookmarkBulkAddRequest,
) => {
  const response = await axiosClient.post<PostBookmarkBulkAddApiResponse>(
    '/v1.0/bookmarks/bulk-add',
    request,
  );
  return response.data;
};

/**
 * 벌크 북마크 추가 Mutation 훅
 *
 * @param options - mutation 옵션
 * @returns 벌크 북마크 추가 mutation
 */
export const usePostBookmarkBulkAddApi = (
  options?: Omit<
    UseMutationOptions<
      PostBookmarkBulkAddApiResponse,
      Error,
      BookmarkBulkAddRequest
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    mutationFn: postBookmarkBulkAddApi,
    ...options,
  });
};
